import React, { useEffect, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import ClientSelectModal from './ClientSelectModal';
import { getUser } from '../../../../lib/storage';
import { tenantService } from '../../../../services/tenantService';
import { Button } from '../../../ui/Button';
import { TextField } from '../../../ui/Field';

interface ConnectTabProps {
  chatbot: any;
}

const ConnectTab: React.FC<ConnectTabProps> = ({ chatbot }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalValue, setModalValue] = useState("");
  const [tenantId, setTenantId] = useState<string>('');
  const [tenantName, setTenantName] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const loadTenant = async () => {
      const user = getUser() as any;
      const resolvedTenantId =
        user?.tenantId ||
        user?.user?.tenantId ||
        localStorage.getItem('agentic_tenant_id') ||
        localStorage.getItem('tenant_id') ||
        '';
      const embeddedTenantName =
        user?.tenant?.name ||
        user?.user?.tenant?.name ||
        user?.tenantName ||
        user?.user?.tenantName ||
        '';

      if (!cancelled) {
        setTenantId(String(resolvedTenantId || ''));
        if (embeddedTenantName) {
          setTenantName(String(embeddedTenantName));
          return;
        }
      }

      if (!resolvedTenantId) return;

      try {
        const tenant = await tenantService.get(String(resolvedTenantId));
        const resolvedTenantName = (tenant as any)?.name || (tenant as any)?.tenantName || '';
        if (!cancelled && resolvedTenantName) {
          setTenantName(String(resolvedTenantName));
        }
      } catch (err) {
        console.error('Failed to load tenant name for connect tab', err);
      }
    };

    loadTenant();
    return () => { cancelled = true; };
  }, []);

  const openModal = (title: string, value: string) => {
    setModalTitle(title);
    setModalValue(value);
    setModalOpen(true);
  };

  // CLIENT_ID is filled in with the logged-in tenant's real ID below once resolved;
  // it stays as a literal placeholder here only until that lookup completes.
  const clientIdToken = tenantId || 'CLIENT_ID';

  // Generate the public chat widget URL
  const publicChatUrl = `${window.location.origin}/chat/${chatbot?.id}/${clientIdToken}`;

  // Generate iframe embed code
  const iframeCode = `<iframe
  src="${window.location.origin}/chat/${chatbot?.id}/${clientIdToken}"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; border-radius: 8px;"
></iframe>`;

  // Generate popup widget script
  const scriptCode = `<script>
  (function() {
    window.AGENTIC_CHAT_CONFIG = {
      chatbotId: '${chatbot?.id}',
      clientId: '${clientIdToken}'
    };
    var s = document.createElement('script');
    s.src = '${window.location.origin}/widget.js';
    s.async = true;
    document.head.appendChild(s);
  })();

  const toggleAgenticOneChatWindow = () => {
    const w = window;
    if (w.AgenticChat) {
      w.AgenticChat.toggle();
    } else {
      console.error('Chat widget is not loaded yet.');
    }
  }
</script>`;

  // Generate popup widget with floating button script
  const floatingButtonScriptCode = `<script>
  (function() {
    window.AGENTIC_CHAT_CONFIG = {
      chatbotId: '${chatbot?.id}',
      clientId: '${clientIdToken}'
    };
    var s = document.createElement('script');
    s.src = '${window.location.origin}/floating-button-widget.js';
    s.async = true;
    document.head.appendChild(s);
  })();
</script>`;

  return (
    <div className="p-6 space-y-6">
      {/* Instructions Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-agentic-text-primary mb-2">
              Integration Guide
            </h3>
            <p className="text-sm text-agentic-text-secondary">
              Follow these steps to integrate the chat widget into your website.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-5 space-y-4">
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Your Client ID
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  {tenantId
                    ? <>Your tenant's Client ID (<code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-xs">{tenantId}</code>) is already filled into every snippet below.</>
                    : "We couldn't detect your tenant ID from this session — contact your administrator to obtain it, then replace CLIENT_ID in the code below manually."}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Choose Integration Method
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Select either <strong>Iframe Embed</strong> (inline widget) or <strong>Popup Widget</strong> (floating button) based on your preference.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Copy the Code
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Click <strong>View &amp; Copy Code</strong> on the snippet you want and copy it — your Client ID is already substituted in.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                4
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Add to Your Website
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Paste the copied code into your website's HTML, preferably just before the closing <code className="bg-gray-100 px-1 py-0.5 rounded">&lt;/body&gt;</code> tag.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">
                ✓
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Test and Verify
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Load your website and verify that the chat widget appears and functions correctly. Test sending a message to ensure everything works.
                </p>
              </div>
            </div>
          </div>
        </div>

        {!tenantId && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-900">
              <strong>⚠️ Important:</strong> No tenant ID was found in your session, so the code below still uses the <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> placeholder. Replace it with your actual Client ID before adding the code to your website — without it, the chat widget will not function.
            </p>
          </div>
        )}
      </div>

      {/* Direct Link */}
      <div className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-agentic-text-primary">Direct Chat Link</h4>
          <div className='flex items-center'>

            <Button tone="custom"
              onClick={() =>
                openModal("Direct Chat Link", publicChatUrl)
              }
              className="
    px-3 py-1
    text-[12px] font-medium
    text-black
    bg-white
    rounded-m
    border
    border-black-500
    rounded-md
  "
              title="View & copy code"
            >
              View &amp; Copy Code
            </Button>


          </div>
        </div>
        <p className="text-sm text-agentic-text-secondary mb-3">
          {tenantId
            ? 'Your Client ID is already filled into this link.'
            : <>Replace <code className="bg-white px-2 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> with your actual Client ID.</>}
        </p>
        <div className="flex gap-2">
          <TextField
            value={publicChatUrl}
            readOnly
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-mono"
          />
        </div>
      </div>


      {/* Iframe Embed */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-agentic-text-primary">Iframe Embed (Inline)</h4>
          <div className='flex items-center'>

            <Button tone="custom"
              onClick={() => openModal("Iframe Embed Code", iframeCode)}
              title="View & copy code"
              className="
  px-3 py-1
  text-[12px] font-medium
  text-black
  bg-white
  rounded-m
  border
  border-black-500
  rounded-md
"
            >
              View &amp; Copy Code
            </Button>



          </div>
        </div>
        <p className="text-sm text-agentic-text-secondary mb-3">
          {tenantId
            ? 'Embed the chat widget inline on your website — your Client ID is already filled in.'
            : <>Embed the chat widget inline on your website. Replace <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">CLIENT_ID</code> with your actual Client ID.</>}
        </p>
        <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-48 relative">
          <code>{iframeCode}</code>
        </pre>
        {!tenantId && (
          <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>Remember to replace <code className="bg-white px-1 py-0.5 rounded font-mono">CLIENT_ID</code> before using this code.</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Script Embed */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-agentic-text-primary">Popup Widget (With Floating Button)</h4>
            <div className='flex items-center'>
              <Button tone="custom"
                onClick={() =>
                  openModal("Popup Widget With Floating Button Script", floatingButtonScriptCode)
                }
                className="
    px-3 py-1
    text-[12px] font-medium
    text-black
    bg-white
    rounded-m
    border
    border-black-500
    rounded-md
  "
                title="View & copy code"
              >
                View &amp; Copy Code
              </Button>

            </div>
          </div>
          <p className="text-sm text-agentic-text-secondary mb-3">
            {tenantId
              ? 'Add as a floating widget with toggle button — your Client ID is already filled in.'
              : <>Add as a floating widget with toggle button. Replace <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">CLIENT_ID</code> with your actual Client ID.</>}
          </p>
          <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-48 relative">
            <code>{floatingButtonScriptCode}</code>
          </pre>
          {!tenantId && (
            <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Remember to replace <code className="bg-white px-1 py-0.5 rounded font-mono">CLIENT_ID</code> before using this code.</span>
            </div>
          )}
        </div>

        {/* Script Embed */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-agentic-text-primary">Popup Widget</h4>
            <div className='flex items-center'>
              <Button tone="custom"
                onClick={() =>
                  openModal("Popup Widget Script", scriptCode)
                }
                className="
    px-3 py-1
    text-[12px] font-medium
    text-black
    bg-white
    rounded-m
    border
    border-black-500
    rounded-md
  "
                title="View & copy code"
              >
                View &amp; Copy Code
              </Button>

            </div>
          </div>
          <p className="text-sm text-agentic-text-secondary mb-3">
            {tenantId
              ? 'Add as a floating widget — your Client ID is already filled in.'
              : <>Add as a floating widget. Replace <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">CLIENT_ID</code> with your actual Client ID.</>}
          </p>
          <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-48 relative">
            <code>{scriptCode}</code>
          </pre>
          {!tenantId && (
            <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Remember to replace <code className="bg-white px-1 py-0.5 rounded font-mono">CLIENT_ID</code> before using this code.</span>
            </div>
          )}
        </div>
      </div>

      {/* Preview Note */}
      <div className="border rounded-lg p-4 bg-blue-50">
        <h4 className="font-medium text-agentic-text-primary mb-2">Preview Note</h4>
        <p className="text-sm text-agentic-text-secondary">
          {tenantId
            ? 'Your Client ID is already filled into the URL/code above — open it in a browser or add it to a test page to try the widget.'
            : <>To test the widget, replace <code className="bg-white px-2 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> in the URL/code with your actual Client ID and open it in a browser or add it to a test page.</>}
        </p>
      </div>
      <ClientSelectModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        value={modalValue}
        tenantId={tenantId}
        tenantName={tenantName}
      />
    </div>
  );
};

export default ConnectTab;
